import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

interface ListningarImagePositionerProps {
  open: boolean;
  imageUrl: string;
  currentPosition?: string; // e.g. "50% 30%"
  onSave: (position: string) => void;
  onClose: () => void;
}

const ListningarImagePositioner: React.FC<ListningarImagePositionerProps> = ({
  open,
  imageUrl,
  currentPosition,
  onSave,
  onClose,
}) => {
  const parsePosition = (pos?: string): { x: number; y: number } => {
    if (!pos) return { x: 50, y: 50 };
    const parts = pos.replace(/%/g, '').split(/\s+/);
    return {
      x: parseFloat(parts[0]) || 50,
      y: parseFloat(parts[1]) || 50,
    };
  };

  const [focalPoint, setFocalPoint] = useState(parsePosition(currentPosition));
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const updateFromEvent = useCallback((clientX: number, clientY: number) => {
    const container = imageContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
    setFocalPoint({ x: Math.round(x), y: Math.round(y) });
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    updateFromEvent(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    updateFromEvent(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleReset = () => {
    setFocalPoint({ x: 50, y: 50 });
  };

  const positionStr = `${focalPoint.x}% ${focalPoint.y}%`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CenterFocusStrongIcon />
          Justera bildposition
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Klicka eller dra i bilden för att välja fokuspunkt. Förhandsvisningen visar hur bilden kommer att beskäras på kortet.
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Originalbild — klicka för att sätta fokuspunkt
            </Typography>
            <Box
              ref={imageContainerRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              sx={{
                position: 'relative',
                cursor: 'crosshair',
                borderRadius: 2,
                overflow: 'hidden',
                border: '2px solid',
                borderColor: 'divider',
                userSelect: 'none',
              }}
            >
              <img
                src={imageUrl}
                alt="Position"
                draggable={false}
                style={{ width: '100%', display: 'block' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: `${focalPoint.x}%`,
                  top: `${focalPoint.y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  border: '3px solid #fff',
                  boxShadow: '0 0 0 2px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)',
                  bgcolor: 'rgba(33,150,243,0.4)',
                  pointerEvents: 'none',
                  transition: isDragging.current ? 'none' : 'left 0.1s, top 0.1s',
                }}
              />
              <Box sx={{
                position: 'absolute',
                left: `${focalPoint.x}%`,
                top: 0,
                bottom: 0,
                width: '1px',
                bgcolor: 'rgba(255,255,255,0.4)',
                pointerEvents: 'none',
              }} />
              <Box sx={{
                position: 'absolute',
                top: `${focalPoint.y}%`,
                left: 0,
                right: 0,
                height: '1px',
                bgcolor: 'rgba(255,255,255,0.4)',
                pointerEvents: 'none',
              }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Position: {focalPoint.x}% {focalPoint.y}%
              </Typography>
              <Button size="small" onClick={handleReset} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
                Centrera
              </Button>
            </Box>
          </Box>

          <Box sx={{ width: { xs: '100%', md: 200 }, flexShrink: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Porträttvy (A4)
            </Typography>
            <Box
              sx={{
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 1,
              }}
            >
              <Box sx={{ aspectRatio: '210 / 297', overflow: 'hidden' }}>
                <img
                  src={imageUrl}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: positionStr,
                  }}
                />
              </Box>
              <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 50, height: 14, bgcolor: '#e0e0e0', borderRadius: 1 }} />
                <Box sx={{ flex: 1 }} />
                <Box sx={{ width: 30, height: 10, bgcolor: '#f0f0f0', borderRadius: 0.5 }} />
              </Box>
            </Box>

            <Typography variant="caption" sx={{ fontWeight: 600, mt: 2, mb: 0.5, display: 'block' }}>
              Landskapsvy
            </Typography>
            <Box
              sx={{
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 1,
              }}
            >
              <Box sx={{ height: 120, overflow: 'hidden' }}>
                <img
                  src={imageUrl}
                  alt="Landscape preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: positionStr,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Avbryt</Button>
        <Button
          variant="contained"
          onClick={() => onSave(positionStr)}
          sx={{ bgcolor: '#072f53', '&:hover': { bgcolor: '#0a4175' }, textTransform: 'none' }}
        >
          Spara position
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ListningarImagePositioner;
